/**
 * Hotel accommodation emission factors by country (Scope 3, Category 6).
 * Units: kgCO2e per room-night
 *
 * Source: CSHB 2023, "Hotel Accommodation Emission Factors by Country", https://cshb.earth/hotel-emission-factors-by-country
 * License: CC BY-SA 4.0, https://creativecommons.org/licenses/by-sa/4.0/
 */

export interface AccommodationFactor {
  /** Country name as provided by the CSHB factor source */
  country: string;
  /** ISO 3166-1 alpha-2 country code (uppercase), matching CountryCombobox codes */
  countryCode: string;
  /** kg CO2e per room-night */
  factor: number;
}

export const ACCOMMODATION_FACTORS_BY_COUNTRY: AccommodationFactor[] = [
    {
        "country": "Algeria",
        "countryCode": "DZ",
        "factor": 57.62
    },
    {
        "country": "Argentina",
        "countryCode": "AR",
        "factor": 29.96
    },
    {
        "country": "Australia",
        "countryCode": "AU",
        "factor": 27.95
    },
    {
        "country": "Austria",
        "countryCode": "AT",
        "factor": 5.05
    },
    {
        "country": "Azerbaijan",
        "countryCode": "AZ",
        "factor": 40.65
    },
    {
        "country": "Bahrain",
        "countryCode": "BH",
        "factor": 77.28
    },
    {
        "country": "Bangladesh",
        "countryCode": "BD",
        "factor": 58.9
    },
    {
        "country": "Belgium",
        "countryCode": "BE",
        "factor": 7.31
    },
    {
        "country": "Brazil",
        "countryCode": "BR",
        "factor": 5.78
    },
    {
        "country": "Bulgaria",
        "countryCode": "BG",
        "factor": 29.54
    },
    {
        "country": "Cambodia",
        "countryCode": "KH",
        "factor": 47.69
    },
    {
        "country": "Canada",
        "countryCode": "CA",
        "factor": 14.51
    },
    {
        "country": "Chile",
        "countryCode": "CL",
        "factor": 19.39
    },
    {
        "country": "China",
        "countryCode": "CN",
        "factor": 41.37
    },
    {
        "country": "Colombia",
        "countryCode": "CO",
        "factor": 9.87
    },
    {
        "country": "Costa Rica",
        "countryCode": "CR",
        "factor": 5.15
    },
    {
        "country": "Croatia",
        "countryCode": "HR",
        "factor": 15.6
    },
    {
        "country": "Czech Republic",
        "countryCode": "CZ",
        "factor": 18.45
    },
    {
        "country": "Denmark",
        "countryCode": "DK",
        "factor": 6.65
    },
    {
        "country": "Dominican Republic",
        "countryCode": "DO",
        "factor": 47.97
    },
    {
        "country": "Ecuador",
        "countryCode": "EC",
        "factor": 16.98
    },
    {
        "country": "Egypt",
        "countryCode": "EG",
        "factor": 46.14
    },
    {
        "country": "Estonia",
        "countryCode": "EE",
        "factor": 34.1
    },
    {
        "country": "Fiji",
        "countryCode": "FJ",
        "factor": 43.01
    },
    {
        "country": "Finland",
        "countryCode": "FI",
        "factor": 6.87
    },
    {
        "country": "France",
        "countryCode": "FR",
        "factor": 3.52
    },
    {
        "country": "French Polynesia",
        "countryCode": "PF",
        "factor": 43.01
    },
    {
        "country": "Georgia",
        "countryCode": "GE",
        "factor": 17.73
    },
    {
        "country": "Germany",
        "countryCode": "DE",
        "factor": 11.01
    },
    {
        "country": "Greece",
        "countryCode": "GR",
        "factor": 20.4
    },
    {
        "country": "Hong Kong, China",
        "countryCode": "HK",
        "factor": 47.0
    },
    {
        "country": "Hungary",
        "countryCode": "HU",
        "factor": 12.57
    },
    {
        "country": "Iceland",
        "countryCode": "IS",
        "factor": 4.25
    },
    {
        "country": "India",
        "countryCode": "IN",
        "factor": 56.06
    },
    {
        "country": "Indonesia",
        "countryCode": "ID",
        "factor": 43.67
    },
    {
        "country": "Ireland",
        "countryCode": "IE",
        "factor": 15.88
    },
    {
        "country": "Israel",
        "countryCode": "IL",
        "factor": 49.37
    },
    {
        "country": "Italy",
        "countryCode": "IT",
        "factor": 15.75
    },
    {
        "country": "Jamaica",
        "countryCode": "JM",
        "factor": 49.71
    },
    {
        "country": "Japan",
        "countryCode": "JP",
        "factor": 29.04
    },
    {
        "country": "Jordan",
        "countryCode": "JO",
        "factor": 42.62
    },
    {
        "country": "Kazakhstan",
        "countryCode": "KZ",
        "factor": 36.76
    },
    {
        "country": "Kenya",
        "countryCode": "KE",
        "factor": 9.9
    },
    {
        "country": "Korea",
        "countryCode": "KR",
        "factor": 34.44
    },
    {
        "country": "Kuwait",
        "countryCode": "KW",
        "factor": 67.05
    },
    {
        "country": "Latvia",
        "countryCode": "LV",
        "factor": 6.51
    },
    {
        "country": "Lebanon",
        "countryCode": "LB",
        "factor": 46.0
    },
    {
        "country": "Lithuania",
        "countryCode": "LT",
        "factor": 5.73
    },
    {
        "country": "Luxembourg",
        "countryCode": "LU",
        "factor": 6.05
    },
    {
        "country": "Macau, China",
        "countryCode": "MO",
        "factor": 70.95
    },
    {
        "country": "Malaysia",
        "countryCode": "MY",
        "factor": 46.97
    },
    {
        "country": "Maldives",
        "countryCode": "MV",
        "factor": 38.85
    },
    {
        "country": "Mauritius",
        "countryCode": "MU",
        "factor": 77.62
    },
    {
        "country": "Mexico",
        "countryCode": "MX",
        "factor": 20.02
    },
    {
        "country": "Morocco",
        "countryCode": "MA",
        "factor": 35.75
    },
    {
        "country": "Namibia",
        "countryCode": "NA",
        "factor": 5.31
    },
    {
        "country": "Netherlands",
        "countryCode": "NL",
        "factor": 10.94
    },
    {
        "country": "New Zealand",
        "countryCode": "NZ",
        "factor": 7.07
    },
    {
        "country": "Nigeria",
        "countryCode": "NG",
        "factor": 43.11
    },
    {
        "country": "Norway",
        "countryCode": "NO",
        "factor": 3.12
    },
    {
        "country": "Oman",
        "countryCode": "OM",
        "factor": 42.11
    },
    {
        "country": "Panama",
        "countryCode": "PA",
        "factor": 25.55
    },
    {
        "country": "Peru",
        "countryCode": "PE",
        "factor": 14.78
    },
    {
        "country": "Philippines",
        "countryCode": "PH",
        "factor": 44.58
    },
    {
        "country": "Poland",
        "countryCode": "PL",
        "factor": 26.11
    },
    {
        "country": "Portugal",
        "countryCode": "PT",
        "factor": 8.02
    },
    {
        "country": "Puerto Rico, USA",
        "countryCode": "PR",
        "factor": 42.65
    },
    {
        "country": "Qatar",
        "countryCode": "QA",
        "factor": 76.35
    },
    {
        "country": "Romania",
        "countryCode": "RO",
        "factor": 12.85
    },
    {
        "country": "Russian Federation",
        "countryCode": "RU",
        "factor": 23.86
    },
    {
        "country": "Rwanda",
        "countryCode": "RW",
        "factor": 17.8
    },
    {
        "country": "Saudi Arabia",
        "countryCode": "SA",
        "factor": 57.75
    },
    {
        "country": "Serbia",
        "countryCode": "RS",
        "factor": 38.41
    },
    {
        "country": "Seychelles",
        "countryCode": "SC",
        "factor": 77.62
    },
    {
        "country": "Singapore",
        "countryCode": "SG",
        "factor": 25.64
    },
    {
        "country": "Slovak Republic",
        "countryCode": "SK",
        "factor": 10.76
    },
    {
        "country": "South Africa",
        "countryCode": "ZA",
        "factor": 38.63
    },
    {
        "country": "Spain",
        "countryCode": "ES",
        "factor": 8.95
    },
    {
        "country": "Sweden",
        "countryCode": "SE",
        "factor": 4.25
    },
    {
        "country": "Switzerland",
        "countryCode": "CH",
        "factor": 3.9
    },
    {
        "country": "Taiwan, China",
        "countryCode": "TW",
        "factor": 43.49
    },
    {
        "country": "Tanzania",
        "countryCode": "TZ",
        "factor": 25.7
    },
    {
        "country": "Thailand",
        "countryCode": "TH",
        "factor": 30.33
    },
    {
        "country": "Tunisia",
        "countryCode": "TN",
        "factor": 46.68
    },
    {
        "country": "Turkey",
        "countryCode": "TR",
        "factor": 30.83
    },
    {
        "country": "Ukraine",
        "countryCode": "UA",
        "factor": 20.79
    },
    {
        "country": "United Arab Emirates",
        "countryCode": "AE",
        "factor": 39.23
    },
    {
        "country": "United Kingdom",
        "countryCode": "GB",
        "factor": 9.4
    },
    {
        "country": "United States",
        "countryCode": "US",
        "factor": 16.12
    },
    {
        "country": "Uruguay",
        "countryCode": "UY",
        "factor": 8.37
    },
    {
        "country": "Uzbekistan",
        "countryCode": "UZ",
        "factor": 47.58
    },
    {
        "country": "Vietnam",
        "countryCode": "VN",
        "factor": 43.16
    },
    {
        "country": "Zambia",
        "countryCode": "ZM",
        "factor": 9.37
    }
];
